import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLogin } from "../../queries/auth";
import { SignInDetailsModel } from "../../types/auth";
import { useAuthStore } from "../../store/useAuthStore";
import { decodeAuthToken } from "../../utils/auth";
import { toast } from "react-toastify";
import AuthLayout from "../../components/component/AuthLayout";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

function UrlParamSync({ searchParams }: { searchParams: URLSearchParams }) {
  const { values, setFieldValue } = useFormikContext<LoginFormValues>();

  useEffect(() => {
    const id = searchParams.get("id");
    if (id && id !== values.email) {
      const decodedEmail = decodeURIComponent(id).replaceAll(" ", "+");
      setFieldValue("email", decodedEmail);
    }

    const key = searchParams.get("key");
    if (key && key !== values.password) {
      setFieldValue("password", key);
    }
  }, [searchParams, setFieldValue, values.email, values.password]);

  return null;
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Please enter a valid email address").required("Email is required"),
  password: Yup.string().required("Password is required"),
  rememberMe: Yup.boolean(),
});

export default function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mutateAsync: login } = useLogin();
  const { setAuth } = useAuthStore();

  const [showPassword, setShowPassword] = useState(false);

  const initialValues: LoginFormValues = {
    email: "",
    password: "",
    rememberMe: false,
  };

  // Admin-generated credentials flow: email/password arrive as ?id=&key= query params.
  // This is a security tradeoff — credentials are briefly visible in the URL.
  // We clean the URL immediately via replaceState to minimize exposure.
  useEffect(() => {
    const id = searchParams.get("id");
    const key = searchParams.get("key");

    // Remove id and key from URL immediately to avoid credential leakage
    if (id || key) {
      searchParams.delete("id");
      searchParams.delete("key");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (values: LoginFormValues, { setSubmitting }: any) => {
    // Decode the email address properly if it comes from URL
    const decodedEmail = decodeURIComponent(values.email).replaceAll(" ", "+");

    const loginDto: SignInDetailsModel = {
      email: decodedEmail,
      password: values.password,
    };

    login(loginDto, {
      onSuccess: (data) => {
        const accessToken = data.data.token;
        const decodedJwt = decodeAuthToken(accessToken);
        if (!decodedJwt) {
          toast.error("Failed to decode authentication token.");
          return;
        }
        setAuth({
          accessToken,
          isAuthenticated: true,
          email: decodedJwt.email,
          id: decodedJwt.userId,
          role: decodedJwt.role,
          clientId: decodedJwt.clientId,
        });

        toast("Logged in successfully!", {
          type: "success",
          autoClose: 2000,
        });

        navigate("/dashboard");
      },
      onError: (data: any) => {
        const errorMessage = data?.response?.data?.errorMessage;
        if (errorMessage === "EMAIL_NOT_VERIFIED") {
          toast.info("Please verify your email to continue.");
          navigate("/verify-email", { state: { email: decodedEmail } });
          return;
        }
        if (errorMessage === "ACCOUNT_DEACTIVATED") {
          navigate("/deactivated");
          return;
        }
        const message =
          errorMessage || data?.message || "Login failed. Please try again.";
        toast.error(message);
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  };

  return (
    <AuthLayout>
      <div>
        {/* Sign In / Sign Up Tabs */}
        <div className="flex p-1 mb-5 bg-gray-100 rounded-lg">
          <button
            type="button"
            className="flex-1 py-2 text-sm font-semibold text-center rounded-md bg-primary text-white shadow-sm transition-all"
          >
            Sign In
          </button>
          <Link
            to="/signup"
            className="flex-1 py-2 text-sm font-semibold text-center rounded-md text-gray-500 hover:text-gray-700 transition-all"
          >
            Sign Up
          </Link>
        </div>

        <h1 className="text-2xl font-bold mb-1">Welcome Back</h1>
        <p className="text-gray-500 text-sm mb-5">Enter Your Credentials to Sign In</p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <UrlParamSync searchParams={searchParams} />
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Email
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
                  placeholder="you@example.com"
                />
                <ErrorMessage name="email" component="p" className="text-red-500 text-xs mt-1" />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <Field
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow pr-20"
                    placeholder="********"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium select-none"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <ErrorMessage name="password" component="p" className="text-red-500 text-xs mt-1" />
              </div>

              <div className="flex justify-between items-center">
                {/* <div className="flex items-center">
                    <Field
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      className="h-3.5 w-3.5 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label
                      htmlFor="rememberMe"
                      className="ml-2 block text-xs text-gray-600 dark:text-gray-300"
                    >
                      Remember me
                    </label>
                  </div> */}
                <div></div>
                <Link to="/forgotPassword" className="text-xs text-primary hover:underline">
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white font-semibold py-2.5 px-4 rounded-lg hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing In..." : "Sign In"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
}
